// ═══════════════════════════════════════════════════════════════════════════════
// Main navigation menu (settings, language, main app links, logout)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { settingsApi, pairingImportApi, systemMessageApi } from '../lib/api';
import { APP_ICONS } from '../lib/iconAssets';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { MainMenuAccordionNav } from './MainMenuAccordionNav';
import {
  buildEmployerPairedMenu,
  buildHelperPairedMenu,
  getAutoExpandedSectionIds,
} from './mainMenuConfig';

type MainMenuButtonProps = {
  panelSide?: 'left' | 'right';
  /** Fixed pixel trigger so header chrome does not scale with user font-size setting. */
  triggerSize?: 'default' | 'header';
};

const TRIGGER_BUTTON_SIZE_CLASS = {
  default: 'h-10 w-10',
  header: 'h-[40px] w-[40px]',
} as const;

const TRIGGER_ICON_SIZE_CLASS = {
  default: 'h-6 w-6',
  header: 'h-[24px] w-[24px]',
} as const;

export const MainMenuButton = ({ panelSide = 'right', triggerSize = 'default' }: MainMenuButtonProps) => {
  const [open, setOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(new Set());
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const [unreadSystemMessageCount, setUnreadSystemMessageCount] = useState(0);

  const isEmployer = user?.role === USER_ROLE.EMPLOYER;
  const isPaired = Boolean(user?.pairedAt && user.pairedAt.trim() !== '');
  const showFullMenu = isPaired || isEmployer;
  const [showInviteReferrer, setShowInviteReferrer] = useState(false);
  const [showPreviousPairingImport, setShowPreviousPairingImport] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      setShowInviteReferrer(false);
      return;
    }
    let cancelled = false;
    void settingsApi
      .getInvitationCodeReferrerStats()
      .then((response) => {
        if (!cancelled) {
          setShowInviteReferrer(Boolean(response.success && response.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShowInviteReferrer(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || user.role !== USER_ROLE.EMPLOYER) {
      setShowPreviousPairingImport(false);
      return;
    }
    let cancelled = false;
    void pairingImportApi
      .getModal()
      .then((response) => {
        if (!cancelled) {
          setShowPreviousPairingImport(Boolean(response.success && response.data?.menuVisible));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShowPreviousPairingImport(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role]);

  const pairedMenuItems = useMemo(() => {
    if (!showFullMenu) return [];
    return isEmployer
      ? buildEmployerPairedMenu(showInviteReferrer, showPreviousPairingImport)
      : buildHelperPairedMenu(showInviteReferrer);
  }, [isEmployer, showFullMenu, showInviteReferrer, showPreviousPairingImport]);

  useEffect(() => {
    if (!showFullMenu || !user?.id) {
      setUnreadSystemMessageCount(0);
      return;
    }
    if (!open) {
      return;
    }
    let cancelled = false;
    void systemMessageApi
      .list(language)
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (!response.success || !response.data) {
          setUnreadSystemMessageCount(0);
          return;
        }
        const unreadCount = response.data.items.filter((item) => !item.isRead).length;
        setUnreadSystemMessageCount(unreadCount);
      })
      .catch(() => {
        if (!cancelled) {
          setUnreadSystemMessageCount(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, showFullMenu, user?.id, language]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setExpandedSectionIds(new Set());
      return;
    }
    setExpandedSectionIds(getAutoExpandedSectionIds(pairedMenuItems, location));
  }, [open, pairedMenuItems, location]);

  const handleClose = () => setOpen(false);

  const handleOpenLogoutConfirm = () => {
    setLogoutConfirmOpen(true);
  };

  const handleCloseLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
  };

  const handleConfirmLogout = async () => {
    await logout();
    setLogoutConfirmOpen(false);
    setOpen(false);
    window.location.href = '/';
  };

  const handleLogoutKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpenLogoutConfirm();
    }
  };

  const handleOpen = () => setOpen(true);

  const handleMenuButtonKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleToggleSection = useCallback((sectionId: string) => {
    setExpandedSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const flatMenuNavItemClass = ({ isActive }: { isActive: boolean }): string =>
    [
      'block rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500',
      isActive
        ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-950/50 dark:text-primary-300'
        : 'font-medium text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800',
    ].join(' ');

  const buttonSizeClass = TRIGGER_BUTTON_SIZE_CLASS[triggerSize];
  const iconSizeClass = TRIGGER_ICON_SIZE_CLASS[triggerSize];

  const menuButtonClassName =
    user?.role === USER_ROLE.HELPER
      ? `inline-flex ${buttonSizeClass} items-center justify-center text-gray-800 transition hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-100 dark:hover:text-white`
      : `inline-flex ${buttonSizeClass} items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800`;

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('settings.mainMenuAria')}
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={handleMenuButtonKeyDown}
        className={menuButtonClassName}
      >
        <span className="sr-only">{t('settings.mainMenuAria')}</span>
        <svg className={iconSizeClass} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {open
        ? createPortal(
            <div
              className={[
                'fixed inset-0 z-50 flex bg-black/40 p-0',
                panelSide === 'left' ? 'justify-start' : 'justify-end',
              ].join(' ')}
              role="presentation"
              onClick={handleClose}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t('settings.mainMenuAria')}
                className="flex h-full w-full max-w-sm flex-col bg-white shadow-xl dark:bg-gray-900 dark:shadow-black/40"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleClose();
                }}
              >
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                  <span className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                    <img src={APP_ICONS.menu} alt="MaidEasy logo" className="h-12 w-12 rounded-md object-contain" />
                    <span>MaidEasy</span>
                  </span>
                  <button
                    type="button"
                    onClick={handleClose}
                    aria-label={t('settings.closePanel')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto px-4 py-4" aria-label={t('settings.mainMenuAria')}>
                  {showFullMenu ? (
                    <MainMenuAccordionNav
                      items={pairedMenuItems}
                      expandedSectionIds={expandedSectionIds}
                      onToggleSection={handleToggleSection}
                      onNavigate={handleClose}
                      unreadSystemMessageCount={unreadSystemMessageCount}
                    />
                  ) : (
                    <ul className="space-y-1">
                      <li>
                        <NavLink to="/pairing/status" className={flatMenuNavItemClass} onClick={handleClose}>
                          {t('settings.menuPairingStatus')}
                        </NavLink>
                      </li>
                      <li>
                        <NavLink to="/settings" end className={flatMenuNavItemClass} onClick={handleClose}>
                          {t('settings.menuSettings')}
                        </NavLink>
                      </li>
                      {showInviteReferrer ? (
                        <li>
                          <NavLink
                            to="/settings/invite-referrer"
                            className={flatMenuNavItemClass}
                            onClick={handleClose}
                          >
                            {t('inviteReferrer.menuItem')}
                          </NavLink>
                        </li>
                      ) : null}
                      <li>
                        <NavLink to="/faq" end className={flatMenuNavItemClass} onClick={handleClose}>
                          {t('home.linkFaq')}
                        </NavLink>
                      </li>
                    </ul>
                  )}
                </nav>

                <div className="border-t border-gray-200 px-4 pt-4 pb-6 space-y-3 dark:border-gray-700">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <Link to="/privacy" onClick={handleClose} className="hover:text-gray-700 hover:underline dark:hover:text-gray-200">
                      {t('home.linkPrivacy')}
                    </Link>
                    <span aria-hidden className="text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                    <Link to="/terms" onClick={handleClose} className="hover:text-gray-700 hover:underline dark:hover:text-gray-200">
                      {t('home.linkTerms')}
                    </Link>
                    <span aria-hidden className="text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                    <Link to="/pics" onClick={handleClose} className="hover:text-gray-700 hover:underline dark:hover:text-gray-200">
                      {t('home.linkPics')}
                    </Link>
                    <span aria-hidden className="text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                    <Link to="/faq" onClick={handleClose} className="hover:text-gray-700 hover:underline dark:hover:text-gray-200">
                      {t('home.linkFaq')}
                    </Link>
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenLogoutConfirm}
                    onKeyDown={handleLogoutKeyDown}
                    tabIndex={0}
                    className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-red-900/60 dark:bg-gray-900 dark:text-red-400 dark:hover:bg-red-950/40"
                  >
                    {t('settings.logout')}
                  </button>
                  <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                    v{import.meta.env.VITE_APP_VERSION}
                    {import.meta.env.VITE_APP_VARIANT}
                  </p>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <ConfirmDialog
        open={logoutConfirmOpen}
        title={t('settings.logoutConfirmTitle')}
        description={t('settings.logoutConfirmDescription')}
        confirmLabel={t('settings.logout')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleConfirmLogout()}
        onCancel={handleCloseLogoutConfirm}
        variant="danger"
        backdropZClass="z-[70]"
      />
    </>
  );
};
